// @flow
import { useEffect, useRef } from 'react';

type Point = { x: number, y: number };

export type Gestures = {
  // A pinch, a trackpad pinch or Ctrl/⌘ + wheel: zoom by `factor` around `at` (screen coordinates)
  onZoomBy: (factor: number, at: Point) => void,
  // A finished touch pinch: the content point that was under `from` is now under `to`
  onPinchEnd: (factor: number, from: Point, to: Point) => void,
  onDoubleTap: (at: Point) => void,
  // The live scale of a touch pinch, before it's committed: clamps it to the zoom's bounds
  clampPinch: (factor: number) => number,
};

const TAP_SLOP = 10;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 30;

const distance = (a: Object, b: Object) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
const midpoint = (a: Object, b: Object): Point => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

// The app's viewport has user-scalable=0, so the WebViews never zoom a page: the viewer zooms itself. A touch
// pinch scales the pages with a CSS transform while the fingers move (no layout, no drawing) and commits once on
// release, when the pages are laid out and drawn at the new size.
export const useZoomGestures = (scrollerRef: { current: ?HTMLElement }, pagesRef: { current: ?HTMLElement }, gestures: Gestures) => {
  const latest = useRef(gestures);
  latest.current = gestures;

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    let pinch = null;
    let tap = null;
    let lastTap = null;
    let safariScale = 1;

    const onTouchStart = (e: Object) => {
      const pages = pagesRef.current;
      if (e.touches.length === 2 && pages) {
        const [a, b] = [e.touches[0], e.touches[1]];
        const from = midpoint(a, b);
        const box = pages.getBoundingClientRect();
        pinch = { start: distance(a, b), from, to: from, factor: 1 };
        tap = null;
        pages.style.transformOrigin = `${from.x - box.left}px ${from.y - box.top}px`;
        e.preventDefault();
      } else if (e.touches.length === 1 && !(e.target && e.target.closest && e.target.closest('button, a'))) {
        // A double tap zooms the pages; two quick taps on a button stay two clicks
        tap = { x: e.touches[0].clientX, y: e.touches[0].clientY, at: Date.now() };
      } else {
        tap = null;
      }
    };

    const onTouchMove = (e: Object) => {
      if (tap && e.touches.length === 1
        && Math.hypot(e.touches[0].clientX - tap.x, e.touches[0].clientY - tap.y) > TAP_SLOP) tap = null;
      const pages = pagesRef.current;
      if (!pinch || e.touches.length < 2 || !pages) return;
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      pinch.factor = latest.current.clampPinch(distance(a, b) / pinch.start);
      pinch.to = midpoint(a, b);
      pages.style.transform = `translate(${pinch.to.x - pinch.from.x}px, ${pinch.to.y - pinch.from.y}px) scale(${pinch.factor})`;
    };

    const onTouchEnd = (e: Object) => {
      if (pinch) {
        if (e.touches.length < 2) {
          const { factor, from, to } = pinch;
          pinch = null;
          latest.current.onPinchEnd(factor, from, to);
        }
        return;
      }
      if (!tap || e.touches.length) return;
      const now = Date.now();
      const here = { x: tap.x, y: tap.y };
      if (lastTap && now - lastTap.at < DOUBLE_TAP_MS && Math.hypot(here.x - lastTap.x, here.y - lastTap.y) < DOUBLE_TAP_SLOP) {
        lastTap = null;
        e.preventDefault();
        latest.current.onDoubleTap(here);
      } else {
        lastTap = { ...here, at: now };
      }
      tap = null;
    };

    // Chrome and Edge send a trackpad pinch as Ctrl + wheel; a mouse sends Ctrl/⌘ + wheel itself
    const onWheel = (e: Object) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      // A mouse notch (100px) is ×1.22; a trackpad's few pixels per event, a smooth zoom
      const delta = Math.max(-100, Math.min(100, e.deltaMode === 1 ? e.deltaY * 33 : e.deltaY));
      latest.current.onZoomBy(Math.exp(-delta * 0.002), { x: e.clientX, y: e.clientY });
    };

    // Safari's trackpad pinch (and, on iOS, alongside the touches, which already zoom)
    const onGestureStart = (e: any) => {
      e.preventDefault();
      safariScale = 1;
    };
    const onGestureChange = (e: any) => {
      e.preventDefault();
      if (pinch || !e.scale) return;
      latest.current.onZoomBy(e.scale / safariScale, { x: e.clientX, y: e.clientY });
      safariScale = e.scale;
    };

    const options = { passive: false };
    scroller.addEventListener('touchstart', onTouchStart, options);
    scroller.addEventListener('touchmove', onTouchMove, options);
    scroller.addEventListener('touchend', onTouchEnd, options);
    scroller.addEventListener('touchcancel', onTouchEnd, options);
    scroller.addEventListener('wheel', onWheel, options);
    scroller.addEventListener('gesturestart', onGestureStart, options);
    scroller.addEventListener('gesturechange', onGestureChange, options);
    return () => {
      scroller.removeEventListener('touchstart', onTouchStart, options);
      scroller.removeEventListener('touchmove', onTouchMove, options);
      scroller.removeEventListener('touchend', onTouchEnd, options);
      scroller.removeEventListener('touchcancel', onTouchEnd, options);
      scroller.removeEventListener('wheel', onWheel, options);
      scroller.removeEventListener('gesturestart', onGestureStart, options);
      scroller.removeEventListener('gesturechange', onGestureChange, options);
    };
  }, [scrollerRef, pagesRef]);
};
